import type { ShowLockedPeriods } from "@components/accounting/schedules/useScheduleLockedPeriodsDialog";
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { undoAccrualReview } from "@api/accrual-review.api";
import { ScheduleLinkedSourceConflictError } from "@api/scheduleErrors";
import { useApplyScheduleTemplateMutation } from "@api/scheduleTemplate.api";
import { ApplyScheduleTemplateBody } from "@common/schemas/domains/accounting/scheduleApplication.schema";
import { ScheduleLinkedSourceConflictErrorBody } from "@common/schemas/domains/accounting/scheduleLinkedSource.schema";
import { getLinkedConflictDocumentType } from "@common/helpers/domains/accounting/scheduleDocumentLinks";
import { capitalize } from "@common/helpers/string";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import {
    applyUndoToAccrualReviewCache,
    invalidateAccrualMatchCaches,
} from "@components/accounting/accrualReview/accrualReviewCache";
import { updateTransactionInCache } from "@components/transactions/useTransactionsQuery";
import { useScheduleLockedPeriodsDialog } from "../useScheduleLockedPeriodsDialog";
import { ScheduleLinkedConflictOption } from "./scheduleLinkedConflictCopy";
import { useScheduleLinkedConflict } from "./useScheduleLinkedConflict";

function linkedTransactionIds(options: {
    payload: ApplyScheduleTemplateBody;
    conflict: ScheduleLinkedSourceConflictErrorBody;
}): number[] {
    if (options.payload.sourceType === ScheduleSourceType.transaction) {
        return [options.payload.transactionId];
    }
    return [
        ...new Set(
            options.conflict.existing.flatMap((application) =>
                application.transactionId == null
                    ? []
                    : [application.transactionId],
            ),
        ),
    ];
}

/** `awaitingChoice`: the conflict modal is open and finishes the apply itself. */
export type ScheduleApplyResult =
    | { outcome: "applied"; applicationId: string }
    | { outcome: "awaitingChoice" };

/** `applicationId` is null when the user kept the existing schedule. */
export type ScheduleAppliedCallback = (result: {
    applicationId: string | null;
}) => Promise<void> | void;

/**
 * Apply that answers a linked-source conflict with the pick-one modal instead
 * of an error. Resolves `applied` with the new schedule when it landed right
 * away; when the modal opens it resolves `awaitingChoice` and the modal
 * finishes the job, calling `onApplied` once the user's choice lands
 * (including keeping the existing schedule, so the surface that asked to
 * apply can close).
 */
export function useApplyScheduleWithLinkedConflict(
    hookOptions: {
        /** Surfaces inside an overlay pass a local dialog; the default opens the global one. */
        showLockedPeriods?: ShowLockedPeriods;
    } = {},
) {
    const { workspaceId } = useScope();
    const { toast, error } = useToaster();
    const queryClient = useQueryClient();
    const mutation = useApplyScheduleTemplateMutation();
    const showLockedPeriods = useScheduleLockedPeriodsDialog(
        hookOptions.showLockedPeriods,
    );
    const { openLinkedConflict, linkedConflictModal } =
        useScheduleLinkedConflict();

    const resolveChoice = useCallback(
        async (options: {
            option: ScheduleLinkedConflictOption;
            templateId: string;
            payload: ApplyScheduleTemplateBody;
            conflict: ScheduleLinkedSourceConflictErrorBody;
            onApplied?: ScheduleAppliedCallback;
        }): Promise<boolean> => {
            const { option, templateId, payload, conflict } = options;
            const documentNoun = getLinkedConflictDocumentType(conflict);
            if (option.kind === "keepExisting") {
                // Nothing to write, but the surface that asked to apply is
                // still open and waits on onApplied to close.
                toast("Kept the existing schedule");
                await options.onApplied?.({ applicationId: null });
                return true;
            }
            if (option.kind === "unmatch" && !workspaceId) {
                error(`Failed to unmatch ${documentNoun} and transaction`);
                return false;
            }
            // Undo commits on its own. Once it has, a failed apply must not
            // leave the unmatch option on offer: undoing again would fail.
            let isUnmatched = false;
            let isUnmatching = false;
            try {
                let applicationId: string;
                if (option.kind === "unmatch" && workspaceId) {
                    isUnmatching = true;
                    for (const transactionId of linkedTransactionIds({
                        payload,
                        conflict,
                    })) {
                        const response = await undoAccrualReview({
                            workspaceId,
                            transactionId,
                        });
                        applyUndoToAccrualReviewCache(response);
                        invalidateAccrualMatchCaches(queryClient, {
                            transactionId,
                        });
                        isUnmatched = true;
                    }
                    isUnmatching = false;
                    const response = await mutation.mutateAsync({
                        templateId,
                        payload,
                    });
                    applicationId = response.application.id;
                    toast(
                        `${capitalize(documentNoun)} unmatched and schedule applied`,
                    );
                } else {
                    const response = await mutation.mutateAsync({
                        templateId,
                        payload: {
                            ...payload,
                            replaceLinkedApplicationIds: conflict.existing.map(
                                (application) => application.applicationId,
                            ),
                        },
                    });
                    applicationId = response.application.id;
                    // The mutation refreshes the side it applied to; the
                    // replaced schedules sat on the other side.
                    invalidateAccrualMatchCaches(queryClient);
                    for (const application of conflict.existing) {
                        if (application.transactionId != null) {
                            updateTransactionInCache(
                                application.transactionId,
                                (transaction) => ({
                                    ...transaction,
                                    schedule: undefined,
                                }),
                            );
                        }
                    }
                    toast("Schedule applied");
                }
                await options.onApplied?.({ applicationId });
                return true;
            } catch (retryError) {
                if (isUnmatching && isUnmatched) {
                    error(
                        `Unmatched only some payments, and couldn't apply the schedule: ${
                            retryError instanceof Error
                                ? retryError.message
                                : "unknown error"
                        }`,
                    );
                    return true;
                }
                if (isUnmatched) {
                    error(
                        `${capitalize(documentNoun)} unmatched, but couldn't apply the schedule: ${
                            retryError instanceof Error
                                ? retryError.message
                                : "unknown error"
                        }`,
                    );
                    return true;
                }
                if (!showLockedPeriods(retryError)) {
                    error(
                        retryError instanceof Error
                            ? retryError.message
                            : "Failed to apply schedule",
                    );
                }
                return false;
            }
        },
        [error, mutation, queryClient, showLockedPeriods, toast, workspaceId],
    );

    const applyWithLinkedConflict = useCallback(
        async (options: {
            templateId: string;
            payload: ApplyScheduleTemplateBody;
            onApplied?: ScheduleAppliedCallback;
        }): Promise<ScheduleApplyResult> => {
            try {
                const response = await mutation.mutateAsync({
                    templateId: options.templateId,
                    payload: options.payload,
                });
                return {
                    outcome: "applied",
                    applicationId: response.application.id,
                };
            } catch (applyError) {
                if (
                    !(applyError instanceof ScheduleLinkedSourceConflictError)
                ) {
                    throw applyError;
                }
                openLinkedConflict({
                    conflict: applyError.details,
                    context: "apply",
                    onChoose: (option) =>
                        resolveChoice({
                            ...options,
                            option,
                            conflict: applyError.details,
                        }),
                });
                return { outcome: "awaitingChoice" };
            }
        },
        [mutation, openLinkedConflict, resolveChoice],
    );

    return {
        applyWithLinkedConflict,
        linkedConflictModal,
        isApplying: mutation.isPending,
    };
}
